import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

/** How far an arrow scrolls the tabs, about two of them. */
const SCROLL_STEP = 240;

type Overflow = { before: boolean; after: boolean };

export type WorkbookTab = { id: string; name: string; icon?: ReactNode };

/** Whether tabs are scrolled out of view to either side of `strip`. */
function measureOverflow(strip: HTMLElement): Overflow {
  return {
    before: strip.scrollLeft > 1,
    after: strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1,
  };
}

/**
 * The open tabs in one row that scrolls sideways when they don't fit. Tabs
 * past either end are said to be there (an arrow on that side) and reachable
 * (the arrows, and a list of every tab), and the tab shown is scrolled into
 * view. A temporary tab is in italics (see `openTab`). A tab's `icon` is the
 * one its item has in the sidebar.
 */
export function WorkbookTabBar({
  tabs,
  selected,
  preview,
  select,
  keep,
  close,
}: {
  tabs: WorkbookTab[];
  selected: string;
  preview: string | undefined;
  select: (id: string) => void;
  keep: (id: string) => void;
  close: (id: string) => void;
}) {
  const gt = useGT();
  const strip = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState<Overflow>({
    before: false,
    after: false,
  });
  useEffect(() => {
    const element = strip.current;
    if (!element) return;
    const update = () => setOverflow(measureOverflow(element));
    update();
    element.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(element);
    for (const child of element.children) observer.observe(child);
    return () => {
      element.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [tabs]);
  // Whether the tabs were scrolled by hand since the tab shown was last
  // brought into view; a resize then leaves them where they were put.
  const scrolledByHand = useRef(false);
  const tabIds = tabs.map((tab) => tab.id).join('\n');
  function reveal() {
    const element = strip.current;
    if (!element) return;
    for (const tab of element.querySelectorAll('[data-tab-id]'))
      if (tab.getAttribute('data-tab-id') === selected)
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
  const revealLatest = useRef(reveal);
  useEffect(() => {
    revealLatest.current = reveal;
  });
  useEffect(() => {
    scrolledByHand.current = false;
    revealLatest.current();
  }, [selected, tabIds]);
  // The bar settles after it first shows (toolbar buttons shrinking to icons,
  // a reload's tabs), which moves the tab shown.
  useEffect(() => {
    const element = strip.current;
    if (!element) return;
    const observer = new ResizeObserver(() => {
      if (!scrolledByHand.current) revealLatest.current();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  function scrollBy(direction: -1 | 1) {
    scrolledByHand.current = true;
    strip.current?.scrollBy({
      left: direction * SCROLL_STEP,
      behavior: 'smooth',
    });
  }
  const overflowing = overflow.before || overflow.after;
  return (
    <div className="flex min-w-48 flex-1 items-stretch self-stretch">
      {overflowing && (
        <ScrollArrow
          side="before"
          disabled={!overflow.before}
          label={gt('Scroll tabs left', { context: 'workbook tab scroll' })}
          onClick={() => scrollBy(-1)}
        />
      )}
      <div
        ref={strip}
        onWheel={() => (scrolledByHand.current = true)}
        onPointerDown={() => (scrolledByHand.current = true)}
        className="-mb-px min-w-0 flex-1 self-stretch overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <TabsList
          className="h-full w-max border-0 bg-transparent pt-2"
          aria-label={gt('Open workbook items', {
            context: 'workbook tab list',
          })}
        >
          {tabs.map(({ id, name, icon }) => {
            const temporary = id === preview;
            return (
              <div
                key={id}
                data-tab-id={id}
                className="group relative flex h-full shrink-0 items-center"
                // Clicking a tab, even the one shown, keeps it open.
                onPointerDown={() => keep(id)}
                onDoubleClick={() => keep(id)}
              >
                <TabsTrigger
                  value={id}
                  title={name}
                  className={cn(
                    'max-w-48 gap-1.5 bg-transparent text-xs data-[active]:mb-0',
                    id !== 'process' && 'pr-7',
                    temporary && 'italic',
                  )}
                >
                  {icon}
                  <span className="truncate">{name}</span>
                </TabsTrigger>
                {id !== 'process' && (
                  <button
                    type="button"
                    className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                    aria-label={gt('Close {name}', {
                      context: 'workbook close tab',
                      name,
                    })}
                    title={gt('Close {name}', {
                      context: 'workbook close tab',
                      name,
                    })}
                    onClick={() => close(id)}
                  >
                    <XIcon size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </TabsList>
      </div>
      {overflowing && (
        <>
          <ScrollArrow
            side="after"
            disabled={!overflow.after}
            label={gt('Scroll tabs right', { context: 'workbook tab scroll' })}
            onClick={() => scrollBy(1)}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 gap-0.5 self-center px-1.5 text-xs text-muted-foreground"
                aria-label={gt('All open tabs', {
                  context: 'workbook tab overflow list',
                })}
              >
                {tabs.length}
                <CaretDownIcon className="size-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="max-h-80 max-w-72 overflow-y-auto"
            >
              {tabs.map(({ id, name, icon }) => (
                <DropdownMenuItem
                  key={id}
                  className={cn(
                    id === selected && 'bg-accent',
                    id === preview && 'italic',
                  )}
                  onClick={() => select(id)}
                >
                  {icon}
                  <span className="truncate" title={name}>
                    {name}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  );
}

function ScrollArrow({
  side,
  disabled,
  label,
  onClick,
}: {
  side: 'before' | 'after';
  disabled: boolean;
  label: string;
  onClick: () => void;
}) {
  const Caret = side === 'before' ? CaretLeftIcon : CaretRightIcon;
  return (
    <Button
      size="icon"
      variant="ghost"
      className={cn(
        'size-6 shrink-0 self-center text-muted-foreground',
        // A shadow on the side with more tabs: the edge they run past.
        !disabled &&
          (side === 'before'
            ? 'shadow-[inset_-6px_0_6px_-6px_var(--color-foreground)]'
            : 'shadow-[inset_6px_0_6px_-6px_var(--color-foreground)]'),
      )}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      <Caret className="size-3.5" />
    </Button>
  );
}
